import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import PasswordInput from '../components/PasswordInput';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { downloadMemberCard } from '../utils/memberCardGenerator';
import './Auth.scss';

const Inscription = () => {
  const { register, user, logout } = useAuth();
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    universite: '',
    filiere: '',
    niveau: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorList, setErrorList] = useState([]);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentPhone, setPaymentPhone] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !user.is_paid) {
      setShowPayment(true);
      setPaymentPhone(user.telephone || '');
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await register(formData);
    
    if (result.success) {
      setError('');
      setErrorList([]);
      if (result.needsPayment) {
        setShowPayment(true);
        setPaymentPhone(formData.telephone);
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
      setErrorList(result.errors || []);
    }
    
    setLoading(false);
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setPaymentLoading(true);
    setError('');

    const normalizedPhone = paymentPhone.replace(/\D/g, '').replace(/^225/, '');

    try {
      const { data } = await api.post('/payment/register', { phone: normalizedPhone });
      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        setError(data.message || 'Erreur lors de l\'initialisation du paiement.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'initialisation du paiement.');
    }
    setPaymentLoading(false);
  };

  if (showPayment) {
    return (
      <div className="auth-page">
        <PageHeader 
          title="Paiement de la cotisation" 
          subtitle="Finalisez votre inscription"
        />
        <div className="auth-container">
          <div className="auth-card" style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div className="auth-header compact">
              <h2>Paiement de la cotisation</h2>
            </div>

            <div className="alert alert-info" style={{ background: '#e8f4fd', color: '#3B5BA5', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Votre compte a été créé avec succès ! Pour finaliser votre inscription et accéder à votre espace membre, 
                vous devez payer votre cotisation de <strong>2 000 FCFA</strong>.
              </p>
            </div>

            {error && (
              <div className="alert alert-error">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                {error}
              </div>
            )}

            {paymentSuccess && (
              <div className="alert alert-success" style={{ background: '#e8f5e9', color: '#28a745', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                {paymentSuccess}
              </div>
            )}

            <form onSubmit={handlePayment} className="auth-form">
              <div className="form-group">
                <label htmlFor="paymentPhone">Numéro de téléphone (Mobile Money) *</label>
                <input
                  type="tel"
                  id="paymentPhone"
                  value={paymentPhone}
                  onChange={(e) => setPaymentPhone(e.target.value)}
                  required
                  placeholder="Ex: 01010101"
                />
                <p style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>
                  Numéro pour recevoir la demande de paiement (Orange, MTN, Moov, Wave...). Saisissez sans l'indicatif +225.
                </p>
              </div>

              <button type="submit" className="auth-submit-btn" disabled={paymentLoading}>
                {paymentLoading ? 'Initialisation...' : 'Payer 2 000 FCFA'}
              </button>

              <div className="auth-footer">
                <p style={{ fontSize: '13px', color: '#888' }}>
                  Vous recevrez une demande de paiement sur votre téléphone. 
                  Validez-la pour finaliser votre inscription.
                </p>
                <p style={{ fontSize: '13px', marginTop: '10px' }}>
                  Mauvais compte ?{' '}
                  <button
                    type="button"
                    onClick={async () => { await logout(); }}
                    style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', textDecoration: 'underline', fontSize: '13px' }}
                  >
                    Se déconnecter
                  </button>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <PageHeader 
        title="Inscription" 
        subtitle="Rejoignez la communauté BE TALENTED"
      />

      <div className="auth-container">
        <div className="auth-split-layout">
          <div className="auth-left-section">
            <div className="auth-content">
              <h2>Devenez Membre</h2>
              <p className="intro-text">
                Rejoignez une communauté d'excellence. En vous inscrivant, vous accédez à :
              </p>
              
              <div className="benefits-list">
                <div className="benefit-item">
                  <div className="benefit-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                      <path d="M2 17l10 5 10-5"/>
                      <path d="M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                  <div className="benefit-text">
                    <h4>Programmes de formation</h4>
                  </div>
                </div>

                <div className="benefit-item">
                  <div className="benefit-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                  </div>
                  <div className="benefit-text">
                    <h4>Mentorat personnalisé</h4>
                  </div>
                </div>

                <div className="benefit-item">
                  <div className="benefit-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="8" r="7"/>
                      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/>
                    </svg>
                  </div>
                  <div className="benefit-text">
                    <h4>Bourses d'études</h4>
                  </div>
                </div>

                <div className="benefit-item">
                  <div className="benefit-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 7h-9"/>
                      <path d="M14 17H5"/>
                      <circle cx="17" cy="17" r="3"/>
                      <circle cx="7" cy="7" r="3"/>
                    </svg>
                  </div>
                  <div className="benefit-text">
                    <h4>Réseau professionnel</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="auth-right-section">
            <div className="auth-card">
              <div className="auth-header compact">
                <h2>Créer un compte</h2>
              </div>

            {error && (
              <div className="alert alert-error" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: '2px' }}>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <div>
                  {errorList.length > 1 ? (
                    <ul style={{ margin: 0, paddingLeft: '16px' }}>
                      {errorList.map((msg, i) => <li key={i}>{msg}</li>)}
                    </ul>
                  ) : (
                    error
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nom">Nom *</label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    placeholder="Votre nom"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="prenom">Prénom *</label>
                  <input
                    type="text"
                    id="prenom"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    required
                    placeholder="Votre prénom"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="votre.email@exemple.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="telephone">Téléphone *</label>
                <input
                  type="tel"
                  id="telephone"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  required
                  placeholder="Ex: 01010101"
                />
              </div>

              <div className="form-group">
                <label htmlFor="universite">Université / École *</label>
                <input
                  type="text"
                  id="universite"
                  name="universite"
                  value={formData.universite}
                  onChange={handleChange}
                  required
                  placeholder="Nom de votre établissement"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="filiere">Filière *</label>
                  <input
                    type="text"
                    id="filiere"
                    name="filiere"
                    value={formData.filiere}
                    onChange={handleChange}
                    required
                    placeholder="Votre filière"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="niveau">Niveau *</label>
                  <select
                    id="niveau"
                    name="niveau"
                    value={formData.niveau}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Sélectionnez</option>
                    <option value="licence1">Licence 1</option>
                    <option value="licence2">Licence 2</option>
                    <option value="licence3">Licence 3</option>
                    <option value="master1">Master 1</option>
                    <option value="master2">Master 2</option>
                    <option value="doctorat">Doctorat</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <PasswordInput
                  id="password"
                  name="password"
                  label="Mot de passe *"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                />

                <PasswordInput
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Confirmer *"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                />
              </div>

              <button type="submit" className="auth-submit-btn" disabled={loading}>
                {loading ? 'Inscription en cours...' : 'S\'inscrire'}
              </button>

              <div className="auth-footer">
                <p>Vous avez déjà un compte ? <Link to="/connexion">Se connecter</Link></p>
              </div>
            </form>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Inscription;
